import { useLazyQuery } from '@apollo/client';
import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEARCH_QUERY } from '../../graphql/resolvers/searchQueries/search.gql';
import './search-bar.scss';

export default function SearchBar() {
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const navigate = useNavigate();

  const [executeSearch, { data, loading }] = useLazyQuery(SEARCH_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length >= 2) {
      debounceRef.current = setTimeout(() => {
        executeSearch({ variables: { query: query.trim(), limit: 5 } });
        setShowResults(true);
      }, 300);
    } else {
      setShowResults(false);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, executeSearch]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const results = data?.search;
  const hasPosts = results?.posts?.length > 0;
  const hasAuthors = results?.authors?.length > 0;
  const hasResults = hasPosts || hasAuthors;

  function handlePostClick(post: any) {
    const pk = post.PK;
    const postId = pk?.replace('POST#', '') || '';
    navigate(`/post/${postId}`);
    setShowResults(false);
    setQuery('');
  }

  function handleAuthorClick(author: any) {
    navigate(`/user/${author.username}/profile`);
    setShowResults(false);
    setQuery('');
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && query.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setShowResults(false);
    }
  }

  function handleClear() {
    setQuery('');
    setShowResults(false);
  }

  return (
    <div className="Search__Bar" ref={wrapperRef}>
      <div className="Search__Bar__Input__Wrapper">
        <Search className="Search__Bar__Icon" />
        <input
          className="Search__Bar__Input"
          type="text"
          placeholder="Search stories and authors"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim().length >= 2 && setShowResults(true)}
        />
        {query && (
          <button
            className="Search__Bar__Clear"
            onClick={handleClear}
            type="button"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showResults && (
        <div className="Search__Results__Dropdown">
          {loading && (
            <div className="Search__Results__Dropdown__Loading">
              Searching...
            </div>
          )}

          {!loading && !hasResults && query.trim().length >= 2 && (
            <div className="Search__Results__Dropdown__Empty">
              No results for &ldquo;{query}&rdquo;
            </div>
          )}

          {hasPosts && (
            <div className="Search__Results__Dropdown__Section">
              <div className="Search__Results__Dropdown__Section__Title">
                Stories
              </div>
              {results.posts.map((post: any) => (
                <div
                  key={`${post.PK}#${post.SK}`}
                  className="Search__Result__Item"
                  onClick={() => handlePostClick(post)}
                >
                  <span className="Search__Result__Item__Title">
                    {post.title}
                  </span>
                  <span className="Search__Result__Item__Meta">
                    <span>@{post.username}</span>
                    {post.category && <span>{post.category}</span>}
                    {post.readTime && <span>{post.readTime} min read</span>}
                  </span>
                </div>
              ))}
            </div>
          )}

          {hasAuthors && (
            <div className="Search__Results__Dropdown__Section">
              <div className="Search__Results__Dropdown__Section__Title">
                Authors
              </div>
              {results.authors.map((author: any) => (
                <div
                  key={`${author.PK}#${author.SK}`}
                  className="Search__Result__Item"
                  onClick={() => handleAuthorClick(author)}
                >
                  <span className="Search__Result__Item__Username">
                    @{author.username}
                  </span>
                  {author.displayName && (
                    <span className="Search__Result__Item__Bio">
                      {author.displayName}
                    </span>
                  )}
                  {author.bio && (
                    <span className="Search__Result__Item__Bio">
                      {author.bio}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {hasResults && (
            <a
              className="Search__View__All"
              href={`/search?q=${encodeURIComponent(query.trim())}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/search?q=${encodeURIComponent(query.trim())}`);
                setShowResults(false);
              }}
            >
              View all results
            </a>
          )}
        </div>
      )}
    </div>
  );
}
